import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback
} from "react";
import debounce from "lodash.debounce";
import { gqlType } from "@hifieng/common";
import { Polyline, Circle } from "react-google-maps";
import useDeepCompareEffect from "use-deep-compare-effect";
import Map, { LatLngBoundsLiteral, MapRenderProps } from "../Map";
import AssetMarkers from "./AssetMarkers";
import PostMarkers from "./PostMarkers";
import { PostMarkerData, CoordinatePoints } from "../../types/EventMap";
import EventMapButtons from "./EventMapButtons";
import CheckboxGroup from "../CheckboxGroup";
import Spiderfy from "./Spiderfy";
import { useQueryParams } from "../../hooks/useQueryParams";
import { useOrganizationContext } from "../../contexts/OrganizationProvider";
import {
  boundsToQueryParams,
  getGeoBounds,
  queryParamsToMapBounds
} from "../../helpers/eventMap";
import { getEnvVariable } from "../../helpers/getEnvVariable";
import { breakpoints } from "../../styles/variables";
import { color, pipelineColorMap } from "../../styles/colors";
import styles from "./index.module.scss";
import { analytics } from "../../analytics";
import PipelineLegend from "./PipelineLegend";
import {
  formatMapEvent,
  PigUpdateWithPipelineRunId,
  TrainUpdateWithPipelineRunId
} from "../../helpers/formatMapEvent";
import { SearchRange } from "../../types/AnalysisTypes";
import { HALF } from "../../helpers/math";
import { useAuth } from "../../contexts/AuthProvider";

type PropsType = {
  events: Array<
    | gqlType.EventNotification
    | gqlType.PigRun
    | PigUpdateWithPipelineRunId
    | gqlType.TrainRun
    | TrainUpdateWithPipelineRunId
  >;
  canSearchOnMapMove: boolean;
  isSearching?: boolean;
  scrollZoom?: boolean;
  focusPipelines?: Array<string>;
  hideLegend?: boolean;
  disableEventIconSelection?: boolean;
  circledRange?: SearchRange;
  trainMode?: boolean;
  ignoreEventForBoundary?: boolean;
};

type SelectedOptionsType = Array<string>;

type PipelineCoordsType = {
  [pipelineId: string]: Array<{ lat: number; lng: number }>;
};

type PipelineBoundsType = {
  north: number;
  south: number;
  east: number;
  west: number;
};

type PipelineBoundsByIdType = {
  [pipelineId: string]: PipelineBoundsType;
};

type SimplifiedPathCacheEntryType = {
  sourcePath: Array<{ lat: number; lng: number }>;
  simplifiedPath: Array<{ lat: number; lng: number }>;
};

type VisibleMapDataType = {
  visibleNonStationAssets: Array<gqlType.Asset>;
  visibleStationAssets: Array<gqlType.Asset>;
  visibleGapPosts: Array<PostMarkerData>;
  visiblePosts: Array<PostMarkerData>;
  visiblePipelines: Array<gqlType.Pipeline>;
};

const initialSelectedOptions = [
  "pipelines",
  "assets",
  "posts",
  "range",
  "gaps",
  "station"
];

const MAX_ZOOM_LEVEL_ASSETS = 10;
const MAX_ZOOM_LEVEL_POSTS = 10;
const DEFAULT_ICON_SIZE = 27;
const SEARCH_DEBOUNCE_TIME = 1000;
// const VISIBILITY_DEBOUNCE_TIME = 200;

const MIN_POINTS_FOR_SIMPLIFICATION = 3;
const ZOOM_LEVEL_COARSE = 8;
const ZOOM_LEVEL_MEDIUM = 10;
const ZOOM_LEVEL_FINE = 12;
const SIMPLIFY_STEP_COARSE = 16;
const SIMPLIFY_STEP_MEDIUM = 8;
const SIMPLIFY_STEP_FINE = 4;
const SIMPLIFY_STEP_FULL = 1;
const SIMPLIFY_COARSE_MULTIPLIER = 2;
const PIPELINE_BOUNDS_PADDING_MULTIPLIER = 0.8;
const MIN_LATITUDE = -90;
const MAX_LATITUDE = 90;
const MIN_LONGITUDE = -180;
const MAX_LONGITUDE = 180;
const FULL_LONGITUDE_SPAN = 360;
const BOUNDS_CACHE_DECIMAL_PLACES = 4;
const MAX_VISIBILITY_CACHE_ENTRIES = 100;
const NO_BOUNDS_CACHE_KEY = "no-bounds";
const VISIBLE_PIPELINES_PER_FRAME = 20;

const polylineOptions = {
  strokeWeight: 5,
  clickable: false
};

const searchLineOptions = {
  ...polylineOptions,
  strokeColor: pipelineColorMap.white
};

const searchCircleOptions = {
  strokeColor: pipelineColorMap.white,
  strokeWeight: 2,
  fillColor: pipelineColorMap.white,
  fillOpacity: 0.1
};

const EventMap = ({
  events,
  canSearchOnMapMove,
  isSearching,
  scrollZoom,
  focusPipelines,
  hideLegend,
  disableEventIconSelection,
  circledRange,
  trainMode,
  ignoreEventForBoundary
}: PropsType) => {
  const { setQueryParams, queryParams } = useQueryParams();

  const { activeOrg } = useOrganizationContext();
  const teckFlag = activeOrg !== undefined && activeOrg.id === "teck";
  const [searchOnMapMove, setSearchOnMapMove] = React.useState(true);
  const [fitBounds, setFitBounds] = useState<LatLngBoundsLiteral>();
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptionsType>(
    initialSelectedOptions
  );
  const [mapChangeCountSinceReset, setMapChangeCountSinceReset] = useState(0);
  const [focusFlag, setFocusFlag] = useState(false);
  const [renderedPipelineCount, setRenderedPipelineCount] = useState(
    VISIBLE_PIPELINES_PER_FRAME
  );

  const pipelines = activeOrg ? activeOrg.pipelines : [];

  const tmFlag = activeOrg ? activeOrg.id === "transmountain_expansion" : false;

  const mapChangedSinceReset = mapChangeCountSinceReset > 1;

  const rangeOption = {
    label: "Selected Range",
    value: "range"
  };

  const { permissions } = useAuth();

  let postLabel: string;
  if (permissions.user.imperial) {
    postLabel = "Mile Posts";
  } else if (activeOrg && activeOrg.id === "enbridge") {
    postLabel = "Posts";
  } else if (permissions.user.station) {
    postLabel = "Station Points";
  } else if (permissions.user.ft) {
    postLabel = "Feet";
  } else {
    postLabel = "KM Posts";
  }

  const defaultOptions = [
    {
      label: trainMode ? "Rails" : "Pipelines",
      value: "pipelines"
    },
    {
      label: "Assets",
      value: "assets"
    },
    {
      label: postLabel,
      value: "posts"
    }
  ];

  if (tmFlag) {
    defaultOptions.push({ label: "Gaps", value: "gaps" });
    defaultOptions.push({ label: "Line 2 Stations", value: "station" });
  }

  const mapOptions = circledRange
    ? [...defaultOptions, rangeOption]
    : defaultOptions;

  const setGeoBoundParams = useCallback(
    (bounds: gqlType.GeoBoundsInput | undefined) => {
      if (canSearchOnMapMove && window.innerWidth > breakpoints.large) {
        if (bounds) {
          setQueryParams(boundsToQueryParams(bounds), "replace");
        } else {
          setQueryParams(
            {
              swLat: undefined,
              swLong: undefined,
              neLat: undefined,
              neLong: undefined
            },
            "replace"
          );
        }
      }
    },
    [canSearchOnMapMove, setQueryParams]
  );

  const toggleMapOption = (optionKey: string) => {
    setSelectedOptions(options => {
      const isSelected = options.includes(optionKey);
      const newSelectedOptions = isSelected
        ? options.filter(option => option !== optionKey)
        : [...options, optionKey];

      return newSelectedOptions;
    });
  };

  const resetPan = (pipelines: Array<gqlType.Pipeline>, focusFlag = false) => {
    // Array of points to be fit within the bounds of the pan/zoom action.
    const points: CoordinatePoints = [];
    pipelines.forEach(pipeline => {
      if (!focusPipelines || focusPipelines.includes(pipeline.id)) {
        if (pipeline.kmPosts) {
          pipeline.kmPosts.forEach(post => points.push(post));
        }
      }
    });
    if (points.length) {
      let geoBounds;
      if (ignoreEventForBoundary) {
        geoBounds = getGeoBounds(points);
      } else {
        geoBounds = getGeoBounds(points, events);
      }

      setFitBounds({
        north: geoBounds.ne.lat,
        east: geoBounds.ne.long,
        south: geoBounds.sw.lat,
        west: geoBounds.sw.long
      });
      setFocusFlag(focusFlag);
      setMapChangeCountSinceReset(0);
    }
  };

  // Debounced because we don't want to update the query params every time the map moves.
  // useRef because we want to make sure we use the most-recently-rendered debounced method,
  // otherwise we would call a newly created debounced method every time
  const searchOnMapMoveRef = useRef(searchOnMapMove);

  useEffect(() => {
    searchOnMapMoveRef.current = searchOnMapMove;
  }, [searchOnMapMove]);

  const onBoundsChanged = useMemo(
    () =>
      debounce((b: gqlType.GeoBoundsInput | null) => {
        if (b && searchOnMapMoveRef.current) {
          setGeoBoundParams(b);
        }
        setMapChangeCountSinceReset(count => {
          return count + 1;
        });
      }, SEARCH_DEBOUNCE_TIME),
    [setGeoBoundParams]
  );

  useEffect(() => {
    return () => {
      onBoundsChanged.cancel();
    };
  }, [onBoundsChanged]);

  // Wait for pipelines to load. If there are query parameters,
  // use them to set the map bounds, otherwise use the bounds
  // generated by the pipeline points
  const initialFocusedPipeline = useMemo(
    () =>
      pipelines.find(
        pipeline => pipeline.interfaceOptions.initialFocus === true
      ),
    [pipelines]
  );

  useDeepCompareEffect(() => {
    const { swLat, swLong, neLat, neLong } = queryParams;
    const hasBoundsQueryParams =
      swLat !== undefined &&
      swLat !== null &&
      swLong !== undefined &&
      swLong !== null &&
      neLat !== undefined &&
      neLat !== null &&
      neLong !== undefined &&
      neLong !== null;

    if (hasBoundsQueryParams) {
      setFitBounds(queryParamsToMapBounds({ swLat, swLong, neLat, neLong }));
    } else if (pipelines.length) {
      if (initialFocusedPipeline !== undefined) {
        resetPan([initialFocusedPipeline], true);
      } else {
        resetPan(pipelines);
      }
    }
    // QueryParams is not a dependency for useEffect because the source of state
    // in this case is the map itself, not the URL. We should set that state when
    // the map loads or when the pipeline state changes but not when the query
    // parameters change, otherwise the application would be trying to sync the
    // two states, the map, and the URL. The URL should reflect the state, but
    // not try to keep the map in sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelines, focusPipelines]);

  // Calculate array of assets
  const assets = useMemo(() => {
    if (!pipelines.length) {
      return [];
    }

    return pipelines.flatMap(pipeline => pipeline.assets);
  }, [pipelines]);

  const uniqueAssets = useMemo(() => {
    const uniqueAssetsById: Map<
      string,
      typeof assets[number]
    > = new window.Map();

    assets.forEach(asset => {
      if (!uniqueAssetsById.has(asset.id)) {
        uniqueAssetsById.set(asset.id, asset);
      }
    });

    return Array.from(uniqueAssetsById.values());
  }, [assets]);

  const nonStationAssets = useMemo(
    () =>
      uniqueAssets.filter(asset => asset.type !== gqlType.AssetType.Station),
    [uniqueAssets]
  );

  const stationAssets = useMemo(
    () =>
      uniqueAssets.filter(asset => asset.type === gqlType.AssetType.Station),
    [uniqueAssets]
  );

  // Calculate array of all pipeline posts with injected post id and pipelineId
  const { posts, coords: pipelineCoords, gapPosts } = useMemo((): {
    posts: Array<PostMarkerData>;
    coords: PipelineCoordsType;
    gapPosts: Array<PostMarkerData>;
  } => {
    const posts: Array<PostMarkerData> = [];
    const gapPosts: Array<PostMarkerData> = [];
    const coords: PipelineCoordsType = {};
    pipelines.forEach(pipeline => {
      if (!coords[pipeline.id]) {
        coords[pipeline.id] = [];
      }

      const hidePosts = pipeline.interfaceOptions.hidePosts ? true : false;
      console.log("hidePosts", hidePosts, pipeline.interfaceOptions.hidePosts);
      console.log("id", pipeline.id);

      pipeline.kmPosts.forEach(post => {
        if (!hidePosts) {
          posts.push({
            id: `${pipeline.id}-${post.post}`,
            pipelineId: pipeline.id,
            ...post
          });

          if (pipeline.color === "white") {
            gapPosts.push({
              id: `${pipeline.id}-${post.post}`,
              pipelineId: pipeline.id,
              ...post
            });
          }
        }

        if (!coords[pipeline.id]) {
          coords[pipeline.id] = [];
        }

        coords[pipeline.id].push({
          lat: post.coordinates.latitude,
          lng: post.coordinates.longitude
        });
      });
    });
    return { posts, coords, gapPosts };
  }, [pipelines]);

  const simplifiedPathCacheRef = useRef<{
    [cacheKey: string]: SimplifiedPathCacheEntryType;
  }>({});

  const visibilityCacheRef = useRef<{
    [cacheKey: string]: VisibleMapDataType;
  }>({});

  useEffect(() => {
    simplifiedPathCacheRef.current = {};
  }, [pipelineCoords]);

  const getSimplifyStepForZoom = (zoomLevel: number): number => {
    if (zoomLevel <= ZOOM_LEVEL_COARSE) {
      return SIMPLIFY_STEP_COARSE * SIMPLIFY_COARSE_MULTIPLIER;
    }

    if (zoomLevel <= ZOOM_LEVEL_MEDIUM) {
      return SIMPLIFY_STEP_MEDIUM;
    }

    if (zoomLevel <= ZOOM_LEVEL_FINE) {
      return SIMPLIFY_STEP_FINE;
    }

    return SIMPLIFY_STEP_FULL;
  };

  const simplifyPath = (
    path: Array<{ lat: number; lng: number }>,
    step: number
  ): Array<{ lat: number; lng: number }> => {
    if (
      step === SIMPLIFY_STEP_FULL ||
      path.length < MIN_POINTS_FOR_SIMPLIFICATION
    ) {
      return path;
    }

    const simplifiedPath: Array<{ lat: number; lng: number }> = [path[0]];
    const lastIndex = path.length - 1;

    for (let index = step; index < lastIndex; index += step) {
      simplifiedPath.push(path[index]);
    }

    simplifiedPath.push(path[lastIndex]);

    return simplifiedPath;
  };

  const getSimplifiedPipelinePath = useCallback(
    (
      pipelineId: string,
      zoomLevel: number
    ): Array<{ lat: number; lng: number }> => {
      const sourcePath = pipelineCoords[pipelineId] || [];
      const step = getSimplifyStepForZoom(zoomLevel);

      if (
        step === SIMPLIFY_STEP_FULL ||
        sourcePath.length < MIN_POINTS_FOR_SIMPLIFICATION
      ) {
        return sourcePath;
      }

      const cacheKey = `${pipelineId}-${step}`;
      const cachedEntry = simplifiedPathCacheRef.current[cacheKey];

      if (cachedEntry && cachedEntry.sourcePath === sourcePath) {
        return cachedEntry.simplifiedPath;
      }

      const simplifiedPath = simplifyPath(sourcePath, step);
      simplifiedPathCacheRef.current[cacheKey] = {
        sourcePath,
        simplifiedPath
      };

      return simplifiedPath;
    },
    [pipelineCoords]
  );

  const loadingStyle = {
    backgroundColor: color.darkMineShaft
  };

  const containerStyle = {
    backgroundColor: color.darkMineShaft
  };

  const mapEvents = useMemo(() => events.map(event => formatMapEvent(event)), [
    events
  ]);

  // computationally optimized Haversine formula
  const distanceFromLatLong = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ) => {
    const p = 0.017453292519943295; // Math.PI / 180
    const c = Math.cos;
    const a =
      HALF -
      c((lat2 - lat1) * p) * HALF +
      c(lat1 * p) * c(lat2 * p) * (1 - c((lon2 - lon1) * p)) * HALF;
    const b = 12742000; // 2 * R * 1000m/km; R = 6371 km

    return b * Math.asin(Math.sqrt(a));
  };

  const getCenter = (
    pipeline: gqlType.Pipeline,
    startChannel: number,
    endChannel: number
  ) => {
    const pipelinePath = pipelineCoords[pipeline.id] || [];
    const coordsValid = pipelinePath[startChannel] && pipelinePath[endChannel];
    const avg = (num1: number, num2: number) => {
      return HALF * (num1 + num2);
    };
    const centerLat = coordsValid
      ? avg(pipelinePath[startChannel].lat, pipelinePath[endChannel].lat)
      : 0;
    const centerLng = coordsValid
      ? avg(pipelinePath[startChannel].lng, pipelinePath[endChannel].lng)
      : 0;
    return { lat: centerLat, lng: centerLng };
  };

  const getApproxSearchRadius = (
    pipeline: gqlType.Pipeline,
    startChannel: number,
    endChannel: number
  ) => {
    const pipelinePath = pipelineCoords[pipeline.id] || [];
    return pipelinePath[startChannel] !== undefined &&
      pipelinePath[endChannel] !== undefined
      ? HALF *
          distanceFromLatLong(
            pipelinePath[startChannel].lat,
            pipelinePath[startChannel].lng,
            pipelinePath[endChannel].lat,
            pipelinePath[endChannel].lng
          )
      : 0;
  };

  const isPostInBounds = (
    post: PostMarkerData,
    bounds: gqlType.GeoBoundsInput | null
  ): boolean => {
    if (!bounds) {
      return true;
    }

    const lat = post.coordinates.latitude;
    const lng = post.coordinates.longitude;
    const west = bounds.sw.long;
    const east = bounds.ne.long;
    const longitudeInBounds =
      west <= east ? lng >= west && lng <= east : lng >= west || lng <= east;

    return lat >= bounds.sw.lat && lat <= bounds.ne.lat && longitudeInBounds;
  };

  const isAssetInBounds = (
    asset: gqlType.Asset,
    bounds: gqlType.GeoBoundsInput | null
  ): boolean => {
    if (!bounds) {
      return true;
    }

    const latitude = asset.coordinates.latitude;
    const longitude = asset.coordinates.longitude;
    const west = bounds.sw.long;
    const east = bounds.ne.long;
    const longitudeInBounds =
      west <= east
        ? longitude >= west && longitude <= east
        : longitude >= west || longitude <= east;

    return (
      latitude >= bounds.sw.lat &&
      latitude <= bounds.ne.lat &&
      longitudeInBounds
    );
  };

  const pipelineBoundsById = useMemo((): PipelineBoundsByIdType => {
    const nextPipelineBoundsById: PipelineBoundsByIdType = {};

    Object.keys(pipelineCoords).forEach(pipelineId => {
      const path = pipelineCoords[pipelineId];

      if (!path || !path.length) {
        return;
      }

      let north = path[0].lat;
      let south = path[0].lat;
      let east = path[0].lng;
      let west = path[0].lng;

      path.forEach(point => {
        if (point.lat > north) {
          north = point.lat;
        }
        if (point.lat < south) {
          south = point.lat;
        }
        if (point.lng > east) {
          east = point.lng;
        }
        if (point.lng < west) {
          west = point.lng;
        }
      });

      nextPipelineBoundsById[pipelineId] = { north, south, east, west };
    });

    return nextPipelineBoundsById;
  }, [pipelineCoords]);

  useEffect(() => {
    visibilityCacheRef.current = {};
  }, [
    nonStationAssets,
    stationAssets,
    gapPosts,
    posts,
    pipelines,
    pipelineBoundsById
  ]);

  const hasLongitudeOverlap = (
    aWest: number,
    aEast: number,
    bWest: number,
    bEast: number
  ): boolean => {
    const boundsCrossesAntimeridian = bWest > bEast;

    if (!boundsCrossesAntimeridian) {
      return !(aWest > bEast || aEast < bWest);
    }

    return !(aWest > bEast && aEast < bWest);
  };

  const isPipelineInBounds = (
    pipelineId: string,
    bounds: gqlType.GeoBoundsInput | null
  ): boolean => {
    if (!bounds) {
      return true;
    }

    const pipelineBounds = pipelineBoundsById[pipelineId];

    if (!pipelineBounds) {
      return false;
    }

    const hasLatitudeOverlap = !(
      pipelineBounds.south > bounds.ne.lat ||
      pipelineBounds.north < bounds.sw.lat
    );

    if (!hasLatitudeOverlap) {
      return false;
    }

    return hasLongitudeOverlap(
      pipelineBounds.west,
      pipelineBounds.east,
      bounds.sw.long,
      bounds.ne.long
    );
  };

  const clampLatitude = (latitude: number): number => {
    return Math.max(MIN_LATITUDE, Math.min(MAX_LATITUDE, latitude));
  };

  const normalizeLongitude = (longitude: number): number => {
    if (longitude < MIN_LONGITUDE) {
      return longitude + FULL_LONGITUDE_SPAN;
    }

    if (longitude > MAX_LONGITUDE) {
      return longitude - FULL_LONGITUDE_SPAN;
    }

    return longitude;
  };

  const getExpandedPipelineBounds = (
    bounds: gqlType.GeoBoundsInput | null
  ): gqlType.GeoBoundsInput | null => {
    if (!bounds) {
      return null;
    }

    const latitudeSpan = bounds.ne.lat - bounds.sw.lat;
    const rawLongitudeSpan = bounds.ne.long - bounds.sw.long;
    const longitudeSpan =
      rawLongitudeSpan >= 0
        ? rawLongitudeSpan
        : rawLongitudeSpan + FULL_LONGITUDE_SPAN;

    const latitudePadding = latitudeSpan * PIPELINE_BOUNDS_PADDING_MULTIPLIER;
    const longitudePadding = longitudeSpan * PIPELINE_BOUNDS_PADDING_MULTIPLIER;

    return {
      sw: {
        lat: clampLatitude(bounds.sw.lat - latitudePadding),
        long: normalizeLongitude(bounds.sw.long - longitudePadding)
      },
      ne: {
        lat: clampLatitude(bounds.ne.lat + latitudePadding),
        long: normalizeLongitude(bounds.ne.long + longitudePadding)
      }
    };
  };

  const getBoundsCacheKey = (bounds: gqlType.GeoBoundsInput | null): string => {
    if (!bounds) {
      return NO_BOUNDS_CACHE_KEY;
    }

    return [
      bounds.sw.lat.toFixed(BOUNDS_CACHE_DECIMAL_PLACES),
      bounds.sw.long.toFixed(BOUNDS_CACHE_DECIMAL_PLACES),
      bounds.ne.lat.toFixed(BOUNDS_CACHE_DECIMAL_PLACES),
      bounds.ne.long.toFixed(BOUNDS_CACHE_DECIMAL_PLACES)
    ].join(":");
  };

  const setVisibilityCacheEntry = (
    cacheKey: string,
    entry: VisibleMapDataType
  ): void => {
    const cacheKeys = Object.keys(visibilityCacheRef.current);

    if (cacheKeys.length >= MAX_VISIBILITY_CACHE_ENTRIES) {
      const oldestKey = cacheKeys[0];
      delete visibilityCacheRef.current[oldestKey];
    }

    visibilityCacheRef.current[cacheKey] = entry;
  };

  const getVisibleMapData = (
    bounds: gqlType.GeoBoundsInput | null
  ): VisibleMapDataType => {
    const cacheKey = getBoundsCacheKey(bounds);
    const cachedEntry = visibilityCacheRef.current[cacheKey];

    if (cachedEntry) {
      return cachedEntry;
    }

    const expandedPipelineBounds = getExpandedPipelineBounds(bounds);

    const nextEntry: VisibleMapDataType = {
      visibleNonStationAssets: nonStationAssets.filter(asset =>
        isAssetInBounds(asset, bounds)
      ),
      visibleStationAssets: stationAssets.filter(asset =>
        isAssetInBounds(asset, bounds)
      ),
      visibleGapPosts: gapPosts.filter(post => isPostInBounds(post, bounds)),
      visiblePosts: posts.filter(post => isPostInBounds(post, bounds)),
      visiblePipelines: pipelines.filter(pipeline =>
        isPipelineInBounds(pipeline.id, expandedPipelineBounds)
      )
    };

    setVisibilityCacheEntry(cacheKey, nextEntry);

    return nextEntry;
  };

  useEffect(() => {
    if (renderedPipelineCount < pipelines.length) {
      const timer = requestAnimationFrame(() => {
        setRenderedPipelineCount(prev =>
          Math.min(prev + VISIBLE_PIPELINES_PER_FRAME, pipelines.length)
        );
      });
      return () => cancelAnimationFrame(timer);
    }

    return undefined;
  }, [renderedPipelineCount, pipelines.length]);

  return (
    <>
      {pipelines.length ? (
        <Map
          googleMapURL={`https://maps.googleapis.com/maps/api/js?key=${getEnvVariable(
            "REACT_APP_GOOGLE_MAPS_API_KEY"
          )}&v=3.40&libraries=geometry,drawing,places`}
          loadingElement={
            <div style={loadingStyle} className={styles.Loading} />
          }
          containerElement={
            <div style={containerStyle} className={styles.Container} />
          }
          mapElement={<div id="EventMap" className={styles.Map} />}
          fitBounds={fitBounds}
          onBoundsChanged={onBoundsChanged}
          scrollZoom={scrollZoom}
        >
          {({ zoomLevel, getMapBounds }: MapRenderProps) => {
            const bounds = getMapBounds();
            const visibleMapData = getVisibleMapData(bounds);
            const {
              visibleNonStationAssets,
              visibleStationAssets,
              visibleGapPosts,
              visiblePosts,
              visiblePipelines
            } = visibleMapData;

            const updateQueryParams = () => {
              if (bounds) {
                setGeoBoundParams(bounds);
              }
            };

            return (
              <>
                <Spiderfy
                  zoomLevel={zoomLevel}
                  events={mapEvents}
                  setFitBounds={setFitBounds}
                  disableEventIconSelection={disableEventIconSelection}
                />
                {selectedOptions.includes("assets") &&
                  zoomLevel >= MAX_ZOOM_LEVEL_ASSETS &&
                  !teckFlag &&
                  visibleNonStationAssets.length > 0 && (
                    <AssetMarkers
                      assets={visibleNonStationAssets}
                      iconSize={DEFAULT_ICON_SIZE}
                    />
                  )}
                {selectedOptions.includes("station") &&
                  zoomLevel >= MAX_ZOOM_LEVEL_ASSETS &&
                  visibleStationAssets.length > 0 && (
                    <AssetMarkers
                      assets={visibleStationAssets}
                      iconSize={DEFAULT_ICON_SIZE}
                    />
                  )}
                {selectedOptions.includes("posts") &&
                  selectedOptions.includes("gaps") &&
                  gapPosts &&
                  zoomLevel >= MAX_ZOOM_LEVEL_POSTS && (
                    <PostMarkers
                      posts={visibleGapPosts}
                      zoomLevel={zoomLevel}
                      bounds={bounds}
                    />
                  )}
                {selectedOptions.includes("posts") &&
                  selectedOptions.includes("pipelines") &&
                  posts &&
                  zoomLevel >= MAX_ZOOM_LEVEL_POSTS && (
                    <PostMarkers
                      posts={visiblePosts}
                      zoomLevel={zoomLevel}
                      bounds={bounds}
                    />
                  )}
                {selectedOptions.includes("pipelines") &&
                  visiblePipelines
                    .slice(0, renderedPipelineCount)
                    .map(pipeline => {
                      if (pipeline.id === "chevron_taylor_ranch_pipeline") {
                        return null;
                      } else if (tmFlag && pipeline.color === "white") {
                        return null;
                      }
                      return (
                        <Polyline
                          key={pipeline.id}
                          path={getSimplifiedPipelinePath(
                            pipeline.id,
                            zoomLevel
                          )}
                          options={{
                            zIndex: pipeline.interfaceOptions.zIndex
                              ? pipeline.interfaceOptions.zIndex
                              : 0,
                            strokeColor:
                              pipelineColorMap[pipeline.color] ||
                              pipelineColorMap.blue,
                            ...polylineOptions
                          }}
                        />
                      );
                    })}
                {selectedOptions.includes("gaps") &&
                  visiblePipelines
                    .slice(0, renderedPipelineCount)
                    .map(pipeline => {
                      if (tmFlag && pipeline.color !== "white") {
                        return null;
                      }
                      return (
                        <Polyline
                          key={pipeline.id}
                          path={getSimplifiedPipelinePath(
                            pipeline.id,
                            zoomLevel
                          )}
                          options={{
                            strokeColor:
                              pipelineColorMap[pipeline.color] ||
                              pipelineColorMap.blue,
                            ...polylineOptions
                          }}
                        />
                      );
                    })}
                {circledRange &&
                  selectedOptions.includes("range") &&
                  focusPipelines &&
                  focusPipelines.length === 1 &&
                  pipelines.map(pipeline => {
                    if (!focusPipelines.includes(pipeline.id)) {
                      return null;
                    }

                    return (
                      <React.Fragment key={pipeline.id}>
                        <Polyline
                          key={`${pipeline.id}-searchRange`}
                          path={pipelineCoords[pipeline.id].slice(
                            circledRange.start,
                            circledRange.end
                          )}
                          options={searchLineOptions}
                        />
                        <Circle
                          key={`${pipeline.id}-circle`}
                          center={getCenter(
                            pipeline,
                            circledRange.start,
                            circledRange.end
                          )}
                          radius={getApproxSearchRadius(
                            pipeline,
                            circledRange.start,
                            circledRange.end
                          )}
                          options={searchCircleOptions}
                        />
                      </React.Fragment>
                    );
                  })}
                <div className={styles.MapOptions}>
                  <CheckboxGroup
                    small
                    name="MapOptions"
                    options={mapOptions}
                    checkboxValue={selectedOptions}
                    onChange={toggleMapOption}
                  />
                  {!hideLegend && (
                    <>
                      <hr className={styles.LegendDivider} />
                      <PipelineLegend
                        pipelines={pipelines}
                        resetPan={resetPan}
                        initialFocusedPipeline={
                          initialFocusedPipeline
                            ? initialFocusedPipeline.id
                            : ""
                        }
                        mapResetFlag={!mapChangedSinceReset && focusFlag}
                      />
                    </>
                  )}
                </div>
                <EventMapButtons
                  canSearchOnMapMove={canSearchOnMapMove}
                  searchOnMapMove={searchOnMapMove}
                  setSearchOnMapMove={shouldSearch => {
                    setSearchOnMapMove(shouldSearch);
                    analytics.mapAutoSearchToggled(shouldSearch ? "on" : "off");
                    if (shouldSearch) {
                      updateQueryParams();
                    }
                  }}
                  showResetMap={mapChangedSinceReset || focusFlag}
                  isSearching={isSearching}
                  onResetMap={() => {
                    resetPan(pipelines);
                    analytics.mapViewReset();
                    setSearchOnMapMove(true);
                    updateQueryParams();
                  }}
                />
              </>
            );
          }}
        </Map>
      ) : null}
    </>
  );
};

const MemoizedEventMap = React.memo(EventMap);
MemoizedEventMap.displayName = "EventMap";

export default MemoizedEventMap;
